import { BriefcaseMedical } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import {
  EntityDropdownMenu,
  Field,
  FieldLayout,
  hasConflicts,
  LinkToEntity,
  renderBadge,
} from "@/src/components/cards/common";
import PractitionerCardEditable from "@/src/components/cards/practitioner-card/practitioner-card-editable";
import ErrorAlert from "@/src/components/error-alert";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canWriteSOR } from "@/src/lib/rbac";
import { Practitioner } from "@/src/models/practitioner";

const PRACTITIONER_FIELDS: Field[] = [
  { key: "familyName", label: "Family Name", comparable: true },
  { key: "middleName", label: "Middle Name", comparable: false },
  { key: "givenName", label: "Given Name", comparable: true },
  { key: "npiNumber", label: "NPI", comparable: true },
  { key: "ehrId", label: "EHR ID", comparable: false },
  { key: "specialty", label: "Specialty", comparable: false },
];

export default function PractitionerCard({
  practitioner,
  type,
  isEditable = false,
  compareMode = false,
  secondaryPractitioners = null,
  onEditPractitioner,
  onEditPractitionerLoading,
}: {
  practitioner: Practitioner;
  type?: "Referring" | "Receiving" | "Primary Care" | "Ordering";
  isEditable?: boolean;
  compareMode?: boolean;
  secondaryPractitioners?: Practitioner[] | null;
  // Callback for when user switches to a different practitioner - allows parent to handle the context update
  onEditPractitioner?: (updatedPractitioner: Practitioner) => Promise<void>;
  onEditPractitionerLoading?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const canEdit = isEditable && canWrite;

  // Find the first non-conflicting secondary practitioner from the array
  // If any match, use that one (will show "Synced")
  // If all conflict, use the first one (will show "Conflicts")
  function findBestMatchingSecondaryPractitioner(
    primary: Practitioner,
    secondaries: Practitioner[] | null
  ): Practitioner | null {
    if (!secondaries || secondaries.length === 0) {
      return null;
    }

    const nonConflicting = secondaries.find((secondary) => !hasConflicts(PRACTITIONER_FIELDS, primary, secondary));

    return nonConflicting ?? secondaries[0];
  }

  const displaySecondaryPractitioner = useMemo(
    () => (secondaryPractitioners ? findBestMatchingSecondaryPractitioner(practitioner, secondaryPractitioners) : null),
    [practitioner, secondaryPractitioners]
  );

  useEffect(() => {
    logger.info("Practitioner in practitioner card", {
      practitioner: practitioner,
      secondaryPractitioner: displaySecondaryPractitioner,
      secondaryPractitionersCount: secondaryPractitioners?.length || 0,
    });
  }, [practitioner, displaySecondaryPractitioner, secondaryPractitioners]);

  let hasAnyConflicts = false;

  if (compareMode && displaySecondaryPractitioner) {
    hasAnyConflicts = hasConflicts(PRACTITIONER_FIELDS, practitioner, displaySecondaryPractitioner);
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <PractitionerCardEditable
        practitioner={practitioner}
        onClose={handleEditToggle}
        type={type}
        onEditPractitioner={onEditPractitioner}
        onEditPractitionerLoading={onEditPractitionerLoading}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: practitioner.id,
    label: "Practitioner ID",
    href: `/providers/${practitioner.displayId}`,
    customProps: canEdit ? [{ label: "Edit Practitioner", onClick: handleEditToggle }] : undefined,
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Card className={`w-full focus:outline-none focus:ring-0`} tabIndex={0}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BriefcaseMedical className="h-6 w-6 mr-2" />
                <CardTitle className="text-md font-semibold">{type ? `${type} Provider` : "Practitioner"}</CardTitle>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {practitioner && renderBadge(compareMode, displaySecondaryPractitioner, hasAnyConflicts)}
                {practitioner && !pathname?.startsWith(`/providers/${practitioner.displayId}`) ? (
                  <LinkToEntity href={`/providers/${practitioner.displayId}`} />
                ) : null}
                {practitioner && canWrite && <EntityDropdownMenu cardActionsProps={cardActionsProps} />}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!practitioner ? (
              <ErrorAlert message="Practitioner not found" compact />
            ) : (
              <FieldLayout
                fields={PRACTITIONER_FIELDS}
                patient={practitioner}
                secondaryObject={displaySecondaryPractitioner}
                compareMode={compareMode}
              />
            )}
          </CardContent>
        </Card>
      </ContextMenuTrigger>
      {practitioner && canWrite && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}

function GridItemSkeleton() {
  return <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
}

export function PractitionerCardSkeleton() {
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center">
          <BriefcaseMedical className="h-6 w-6 mr-2" />
          <CardTitle className="text-md font-semibold">Practitioner Information</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <span className="font-semibold">Given Name:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Family Name:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">NPI:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Specialty:</span> <GridItemSkeleton />
          </div>
          <div>
            <span className="font-semibold">Role:</span> <GridItemSkeleton />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
